/**
 * Classification of everything that can stop the microphone from working, so
 * the UI can show an actionable hint instead of a raw DOMException name.
 */

/** Why the audio pipeline could not start. */
export type AudioErrorKind =
  | 'insecure-context'
  | 'unsupported'
  | 'permission-denied'
  | 'no-device'
  | 'device-busy'
  | 'device-gone'
  | 'unknown'

/** A classified failure with a message meant for humans. */
export class AudioEngineError extends Error {
  constructor(
    readonly kind: AudioErrorKind,
    message: string,
    override readonly cause?: unknown,
  ) {
    super(message)
    this.name = 'AudioEngineError'
  }
}

const MESSAGES: Record<AudioErrorKind, string> = {
  'insecure-context':
    'Mikrofonzugriff braucht einen sicheren Kontext. Den Tuner über HTTPS oder auf localhost öffnen.',
  unsupported: 'Dieser Browser bietet keinen Mikrofonzugriff an (navigator.mediaDevices fehlt).',
  'permission-denied':
    'Der Mikrofonzugriff wurde blockiert. In den Browsereinstellungen für diese Seite erlauben und neu starten.',
  'no-device':
    'Es wurde kein Audioeingang gefunden. Interface oder Mikrofon anschließen und erneut versuchen.',
  'device-busy':
    'Der gewählte Eingang wird von einem anderen Programm benutzt. Dieses schließen oder einen anderen Eingang wählen.',
  'device-gone':
    'Der gewählte Eingang ist nicht mehr verfügbar. Einen anderen aus der Liste wählen und neu starten.',
  unknown: 'Das Mikrofon konnte nicht gestartet werden.',
}

/** Human-readable default text for a failure kind. */
export function messageFor(kind: AudioErrorKind): string {
  return MESSAGES[kind]
}

/** Name of a DOMException-like value, or null when it carries none. */
function errorNameOf(error: unknown): string | null {
  if (error instanceof Error) return error.name
  if (typeof error === 'object' && error !== null && 'name' in error) {
    const { name } = error
    return typeof name === 'string' ? name : null
  }
  return null
}

/**
 * Map a `getUserMedia` rejection onto an {@link AudioErrorKind}.
 *
 * The names follow the Media Capture spec; browsers differ in which one they
 * pick, so several names map to the same kind.
 */
export function classifyMediaError(error: unknown, hasDeviceSelection = false): AudioErrorKind {
  switch (errorNameOf(error)) {
    case 'NotAllowedError':
    case 'SecurityError':
    case 'PermissionDeniedError':
      return 'permission-denied'
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'no-device'
    case 'OverconstrainedError':
    case 'ConstraintNotSatisfiedError':
      // A constraint can only fail here because the stored deviceId is gone.
      return hasDeviceSelection ? 'device-gone' : 'no-device'
    case 'NotReadableError':
    case 'TrackStartError':
    case 'AbortError':
      return 'device-busy'
    default:
      return 'unknown'
  }
}

/** Build a classified error from a `getUserMedia` rejection. */
export function toAudioEngineError(error: unknown, hasDeviceSelection = false): AudioEngineError {
  if (error instanceof AudioEngineError) return error
  const kind = classifyMediaError(error, hasDeviceSelection)
  return new AudioEngineError(kind, messageFor(kind), error)
}
